import { useCallback, useMemo } from 'react';

import { WatchQueryFetchPolicy } from '@apollo/client';
import { useStoreContext } from 'features/store';

import { useLoyaltyServiceRewardQuery } from 'generated/graphql-gateway';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { IEngineRewardsMap, LoyaltyReward } from 'state/loyalty/types';
import { useServiceModeLive } from 'state/service-mode';

import { Reward, RewardsMap } from '../types';
import { mapServiceRewardToReward, rewardToEngineReward, rewardToLoyaltyReward } from '../utils';
import { getLoyaltyServiceRewardServiceMode } from '../utils/service-mode';

interface IUseLoyaltyServiceRewardOptions {
  loyaltyEngineId?: string;
  skip?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
}

export function useLoyaltyServiceReward({
  loyaltyEngineId,
  skip = false,
  fetchPolicy = 'network-only',
}: IUseLoyaltyServiceRewardOptions) {
  const serviceMode = useServiceModeLive();
  const { store } = useStoreContext();
  const { loading: loyaltyUserIdLoading } = useLoyaltyUserId();

  const variables = {
    id: loyaltyEngineId ?? '',
    omitInvalids: false,
    serviceMode: getLoyaltyServiceRewardServiceMode(serviceMode),
    storeId: store?.number,
  };

  const querySkip = skip || loyaltyUserIdLoading || !loyaltyEngineId;

  const { data, error, loading: queryLoading, refetch } = useLoyaltyServiceRewardQuery({
    variables,
    fetchPolicy,
    errorPolicy: 'all',
    skip: querySkip,
  });

  const serviceReward = data?.loyaltyServiceReward;

  const { reward, rewardsMap, selectedReward, engineRewardsMap } = useMemo<{
    reward?: Reward;
    rewardsMap: RewardsMap;
    selectedReward?: LoyaltyReward;
    engineRewardsMap: IEngineRewardsMap;
  }>(() => {
    if (!serviceReward) {
      return {
        reward: undefined,
        rewardsMap: {},
        selectedReward: undefined,
        engineRewardsMap: {},
      };
    }

    const reward = mapServiceRewardToReward(serviceReward);
    return {
      reward,
      rewardsMap: { [reward.id]: reward },
      selectedReward: rewardToLoyaltyReward(reward, serviceReward),
      engineRewardsMap: {
        [reward.id]: rewardToEngineReward(reward, serviceReward),
      },
    };
  }, [serviceReward]);

  const refreshReward = useCallback(async () => {
    if (skip || loyaltyUserIdLoading || !loyaltyEngineId) {
      return;
    }

    return refetch();
  }, [loyaltyEngineId, loyaltyUserIdLoading, refetch, skip]);

  const loading = !skip && (loyaltyUserIdLoading || Boolean(loyaltyEngineId && queryLoading));

  return {
    reward,
    rewardsMap,
    selectedReward,
    engineRewardsMap,
    error,
    loading,
    refreshReward,
  };
}
